import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '../../components/common/Layout/Layout';
import { useCart } from '../../context/CartContext';
import { useToast } from '../../context/ToastContext';
import { formatCurrencyDetailed } from '../../utils/currency';
import ApiImage from '../../components/common/ApiImage';
import { API_URL } from '../../config/api';
import './OrderSuccess.css';

function OrderSuccess() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const { showToast } = useToast();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoiceDownloading, setInvoiceDownloading] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Content-Type': 'application/json',
        };

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_URL}/account/orders/${orderId}`, {
          headers,
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setOrder(data.order);
            // Clear cart after successful order
            clearCart();
          } else {
            setError('Order not found');
          }
        } else {
          setError('Failed to load order details');
        }
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrder();
    } else {
      setError('Invalid order ID');
      setLoading(false);
    }
  }, [orderId, clearCart]);

  const getEstimatedDelivery = () => {
    const deliveryDate = new Date();
    deliveryDate.setDate(deliveryDate.getDate() + 5); // 5 business days
    return deliveryDate.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      upi: 'UPI',
      card: 'Debit/Credit Card',
      netbanking: 'Net Banking',
      wallet: 'Wallet',
      cod: 'Cash on Delivery',
    };
    return labels[method] || method;
  };

  const handleDownloadInvoice = async () => {
    if (!orderId) return;
    setInvoiceDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_URL}/orders/${orderId}/invoice`, { headers });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to download invoice');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition');
      let filename = `invoice-${order?.orderNumber || orderId}.pdf`;
      if (disposition) {
        const match = /filename="?([^";\n]+)"?/.exec(disposition);
        if (match) filename = match[1].trim();
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast('Invoice downloaded', 'success');
    } catch (err) {
      console.error('Invoice download error:', err);
      showToast(err.message || 'Could not download invoice. Try again later.', 'error');
    } finally {
      setInvoiceDownloading(false);
    }
  };

  const handleWhatsAppSupport = () => {
    const message = encodeURIComponent(`Hi, I need help with my order ${order?.orderNumber || orderId}`);
    window.open(`https://wa.me/919876543210?text=${message}`, '_blank');
  };

  if (loading) {
    return (
      <Layout>
        <div className="order-success">
          <div className="order-success__loading">
            <div className="order-success__spinner"></div>
            <p>Loading order details...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !order) {
    return (
      <Layout>
        <div className="order-success">
          <div className="order-success__error">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <h2>Order Not Found</h2>
            <p>{error || 'The order you are looking for does not exist.'}</p>
            <Link to="/" className="btn-primary">
              Continue Shopping
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>Order Confirmed | Luvia</title>
        <meta name="description" content={`Your order ${order.orderNumber} has been confirmed. Thank you for shopping with Luvia.`} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="order-success">
        <div className="order-success__container">
          {/* Success Header */}
          <div className="order-success__header">
            <div className="order-success__icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <h1 className="order-success__title">Order Confirmed!</h1>
            <p className="order-success__subtitle">
              Thank you for your purchase. We've received your order and will begin processing it shortly.
            </p>
            <p className="order-success__reassurance">
              We've sent an order confirmation to your email.
            </p>
          </div>

          {/* Order Details Card */}
          <div className="order-success__card">
            <div className="order-success__card-header">
              <h2 className="order-success__card-title">Order Details</h2>
              <span className="order-success__order-number">#{order.orderNumber}</span>
            </div>

            <div className="order-success__info-grid">
              <div className="order-success__info-item">
                <span className="order-success__info-label">Order Date</span>
                <span className="order-success__info-value">
                  {new Date(order.createdAt).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </div>

              <div className="order-success__info-item">
                <span className="order-success__info-label">Payment Method</span>
                <span className="order-success__info-value">
                  {getPaymentMethodLabel(order.payment?.method)}
                </span>
              </div>

              <div className="order-success__info-item">
                <span className="order-success__info-label">Payment Status</span>
                <span className={`order-success__info-value order-success__info-value--${order.payment?.status}`}>
                  {order.payment?.status === 'paid' ? 'Paid' : order.payment?.status === 'pending' && order.payment?.method === 'cod' ? 'Pending (COD)' : 'Pending'}
                </span>
              </div>

              <div className="order-success__info-item">
                <span className="order-success__info-label">Estimated Delivery</span>
                <span className="order-success__info-value">
                  {getEstimatedDelivery()}
                </span>
              </div>
            </div>

            {/* Shipping Address */}
            {order.address && (
              <div className="order-success__address">
                <h3 className="order-success__address-title">Shipping Address</h3>
                <div className="order-success__address-content">
                  <p>{order.address.fullName}</p>
                  <p>{order.address.phone}</p>
                  <p>{order.address.line1}</p>
                  {order.address.line2 && <p>{order.address.line2}</p>}
                  <p>
                    {order.address.city}, {order.address.state} - {order.address.pincode}
                  </p>
                </div>
              </div>
            )}

            {/* Order Items */}
            <div className="order-success__items">
              <h3 className="order-success__items-title">Items Ordered</h3>
              <div className="order-success__items-list">
                {order.items.map((item, index) => (
                  <div key={index} className="order-success__item">
                    <ApiImage
                      src={item.image?.url || item.image}
                      fallback="/placeholder-jewelry.svg"
                      alt={item.name}
                      className="order-success__item-image"
                      width="80"
                      height="100"
                    />
                    <div className="order-success__item-details">
                      <h4 className="order-success__item-name">{item.name}</h4>
                      <p className="order-success__item-variant">
                        Size: {item.size} · Qty: {item.quantity}
                      </p>
                    </div>
                    <div className="order-success__item-price">
                      {formatCurrencyDetailed(item.price * item.quantity)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Summary */}
            <div className="order-success__summary">
              <div className="order-success__summary-row">
                <span className="order-success__summary-label">Subtotal</span>
                <span className="order-success__summary-value">
                  {formatCurrencyDetailed(order.pricing?.subtotal || 0)}
                </span>
              </div>
              <div className="order-success__summary-row">
                <span className="order-success__summary-label">Shipping</span>
                <span className="order-success__summary-value">
                  {(order.pricing?.shipping || 0) === 0
                    ? 'Free'
                    : formatCurrencyDetailed(order.pricing?.shipping || 0)}
                </span>
              </div>
              {(order.pricing?.discount || 0) > 0 && (
                <div className="order-success__summary-row">
                  <span className="order-success__summary-label">Discount</span>
                  <span className="order-success__summary-value">
                    -{formatCurrencyDetailed(order.pricing?.discount || 0)}
                  </span>
                </div>
              )}
              <div className="order-success__summary-row order-success__summary-row--total">
                <span className="order-success__summary-label">Total</span>
                <span className="order-success__summary-value">
                  {formatCurrencyDetailed(order.pricing?.total || 0)}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="order-success__actions">
            <Link to={`/account/orders`} className="order-success__btn btn-primary">
              Track Order
            </Link>
            <button
              className="order-success__btn btn-ghost"
              onClick={handleDownloadInvoice}
              disabled={invoiceDownloading}
              aria-busy={invoiceDownloading}
            >
              {invoiceDownloading ? 'Downloading…' : 'Download Invoice'}
            </button>
            <Link to="/shop" className="order-success__btn btn-ghost">
              Continue Shopping
            </Link>
          </div>

          {/* Support Section */}
          <div className="order-success__support">
            <h3 className="order-success__support-title">Need Help?</h3>
            <p className="order-success__support-text">
              Our customer support team is here to help you with any questions about your order.
            </p>
            <button
              className="order-success__whatsapp-btn"
              onClick={handleWhatsAppSupport}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              Chat on WhatsApp
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default OrderSuccess;
